/**
 * Monthly Cashflow chart (dashboards round 3: calendar-year axis): shared
 * between the Overall dashboard (group scope) and the entity/Personal
 * dashboard.
 *
 * Bars = monthly net cashflow, cash basis (month-over-month bank balance
 * change from transactions) -- the same calendar_year_months figures the
 * "Avg Net Cashflow (YTD, complete months)" card averages, EXCEPT that card
 * drops the last (current, possibly partial) bar from its mean while every
 * bar including the current one still renders here -- see
 * computeAvgNetCashflow's own comment. The card does NOT equal the mean of
 * every rendered bar; it equals the mean of all but the last one. A future
 * month's `null` bar renders as an empty gap (no Cell fill), not a zero.
 * Line overlay = net margin from the P&L predicate, so the P&L-vs-cash
 * divergence (financing, intercompany timing, VAT) is visible instead of
 * confusing. Both series are labelled explicitly so they are never mistaken
 * for one another.
 */
import {
  ResponsiveContainer, ComposedChart, Bar, Cell, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, LabelList,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCompact, formatCurrencyFull } from '../../lib/chartFormatters';

const POSITIVE_COLOR = '#10B981';
const NEGATIVE_COLOR = '#DC2626';
const NET_MARGIN_COLOR = '#8B5CF6';

export function MonthlyCashflowChart({ chartData, displayCurrency }) {
  if (!chartData || chartData.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Monthly Cashflow</CardTitle>
        <p className="text-xs text-muted-foreground">
          Net cashflow (cash basis, bars) vs. Net Margin (P&L, line) -- these legitimately
          diverge: cashflow includes financing, intercompany timing, and VAT.
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={chartData} margin={{ top: 24, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v) => formatCompact(v, displayCurrency)} tick={{ fontSize: 11 }} width={60} />
            <Tooltip
              formatter={(val, name) => [val == null ? 'No data' : formatCurrencyFull(val, displayCurrency), name]}
              contentStyle={{ fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="netCashflow" name="Net Cashflow (cash basis)">
              {chartData.map((row, i) => (
                <Cell key={row._monthKey || i} fill={row.netCashflow == null ? 'transparent' : (row.netCashflow >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR)} />
              ))}
              <LabelList
                dataKey="netCashflow"
                position="top"
                fontSize={10}
                formatter={(v) => formatCompact(v, displayCurrency)}
              />
            </Bar>
            <Line
              type="monotone"
              dataKey="netMargin"
              name="Net Margin (P&L)"
              stroke={NET_MARGIN_COLOR}
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
